import { ChevronDown, Info } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  EXTERNAL_EMR_CHARTS_LABEL,
  EXTERNAL_EMR_VIEW_HELP,
  ROGER_CHARTS_LABEL,
} from './externalEmr/externalEmrPatientCopy';

type PatientPopulation = 'inactive' | 'test' | 'imported';

const POPULATION_OPTIONS: {
  value: PatientPopulation;
  label: string;
}[] = [
  { value: 'inactive', label: 'Show inactive patients' },
  { value: 'test', label: 'Show test patients' },
  { value: 'imported', label: 'Show imported patients' },
];

const POPULATION_SHORT: Record<PatientPopulation, string> = {
  inactive: 'inactive',
  test: 'test',
  imported: 'imported',
};

/** Closed-state summary. Unselected populations stay off the list. */
function populationTriggerLabel(selected: PatientPopulation[]): string {
  if (selected.length === 0) return 'Active patients';
  if (selected.length === POPULATION_OPTIONS.length) return 'All patients';
  return `Active + ${selected.map(value => POPULATION_SHORT[value]).join(', ')}`;
}

function selectedPopulations({
  showInactive,
  hideTest,
  hideImported,
}: {
  showInactive: boolean;
  hideTest: boolean;
  hideImported: boolean;
}): PatientPopulation[] {
  const selected: PatientPopulation[] = [];
  if (showInactive) selected.push('inactive');
  if (!hideTest) selected.push('test');
  if (!hideImported) selected.push('imported');
  return selected;
}

export interface PatientManagerFilterOption {
  value: string;
  label: string;
}

/**
 * A checklist dropdown for one server filter. Nothing ticked means every
 * value; the closed label carries the count; Clear empties it. When the
 * options failed to load the menu says so and offers a retry instead.
 */
function ChecklistFilter({
  name,
  options,
  selected,
  onChange,
  testId,
  error,
  onRetry,
}: {
  name: string;
  options: PatientManagerFilterOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  testId: string;
  error?: string | null;
  onRetry?: () => void;
}) {
  const chosen = new Set(selected);
  const label = chosen.size === 0 ? name : `${name}: ${chosen.size} selected`;
  const toggle = (value: string, checked: boolean) =>
    onChange(
      checked
        ? [...selected, value]
        : selected.filter(candidate => candidate !== value)
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type='button'
          variant='outline'
          data-testid={testId}
          aria-label={label}
          className='w-full min-w-0 justify-between sm:w-auto'
        >
          <span className='truncate'>{label}</span>
          <ChevronDown className='opacity-50' aria-hidden='true' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='start'
        className='max-h-80 w-64 overflow-y-auto'
      >
        <DropdownMenuLabel>{name}</DropdownMenuLabel>
        {error ? (
          <>
            <DropdownMenuItem disabled>
              Couldn't load {name.toLowerCase()} options
            </DropdownMenuItem>
            {onRetry && (
              <DropdownMenuItem onSelect={() => onRetry()}>
                Retry
              </DropdownMenuItem>
            )}
          </>
        ) : options.length === 0 ? (
          <DropdownMenuItem disabled>No options</DropdownMenuItem>
        ) : (
          options.map(option => (
            <DropdownMenuCheckboxItem
              key={option.value}
              checked={chosen.has(option.value)}
              onSelect={event => event.preventDefault()}
              onCheckedChange={checked =>
                toggle(option.value, checked === true)
              }
            >
              <span className='truncate'>{option.label}</span>
            </DropdownMenuCheckboxItem>
          ))
        )}
        {chosen.size > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onChange([])}>
              Clear
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface PatientManagerListFiltersProps {
  showInactive: boolean;
  hideTest: boolean;
  hideImported: boolean;
  onShowInactive: (show: boolean) => void;
  onHideTest: (hide: boolean) => void;
  onHideImported: (hide: boolean) => void;
  /** Current-episode payer and city filters (Roger charts only). */
  insuranceOptions: PatientManagerFilterOption[];
  /** Why the payer list is missing, if it failed; Retry asks again. */
  insuranceError?: string | null;
  onRetryInsurances?: () => void;
  insuranceIds: string[];
  onInsuranceIds: (ids: string[]) => void;
  cityOptions: string[];
  cities: string[];
  onCities: (cities: string[]) => void;
  /** Agencies mid-migration can switch the whole list. */
  showViewSelector: boolean;
  externalEmrView: boolean;
  onExternalEmrView: (on: boolean) => void;
}

/**
 * Roger EMR population lives in one multi-select. The External EMR switch is
 * a separate two-choice selector and replaces the population menu, because
 * that view is the census, not a filter on Roger charts.
 */
export function PatientManagerListFilters({
  showInactive,
  hideTest,
  hideImported,
  onShowInactive,
  onHideTest,
  onHideImported,
  insuranceOptions,
  insuranceError,
  onRetryInsurances,
  insuranceIds,
  onInsuranceIds,
  cityOptions,
  cities,
  onCities,
  showViewSelector,
  externalEmrView,
  onExternalEmrView,
}: PatientManagerListFiltersProps) {
  const selected = selectedPopulations({
    showInactive,
    hideTest,
    hideImported,
  });
  const label = populationTriggerLabel(selected);
  const inExternalEmrView = showViewSelector && externalEmrView;

  const onPopulationChecked = (value: PatientPopulation, checked: boolean) => {
    if (value === 'inactive') onShowInactive(checked);
    else if (value === 'test') onHideTest(!checked);
    else onHideImported(!checked);
  };

  return (
    <div className='flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center'>
      {!inExternalEmrView && (
        <ChecklistFilter
          name='Insurance'
          options={insuranceOptions}
          selected={insuranceIds}
          onChange={onInsuranceIds}
          testId='patient-insurance-filter'
          error={insuranceError}
          onRetry={onRetryInsurances}
        />
      )}
      {!inExternalEmrView && (
        <ChecklistFilter
          name='City'
          options={cityOptions.map(city => ({ value: city, label: city }))}
          selected={cities}
          onChange={onCities}
          testId='patient-city-filter'
        />
      )}
      {!inExternalEmrView && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type='button'
              variant='outline'
              data-testid='patient-population-filter'
              aria-label={`Show patients: ${label}`}
              title={label}
              className='w-full min-w-0 justify-between sm:w-auto'
            >
              <span className='truncate'>{label}</span>
              <ChevronDown className='opacity-50' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='start' className='min-w-56'>
            <DropdownMenuLabel>Show patients</DropdownMenuLabel>
            {POPULATION_OPTIONS.map(option => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={selected.includes(option.value)}
                onSelect={event => event.preventDefault()}
                onCheckedChange={checked =>
                  onPopulationChecked(option.value, checked === true)
                }
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {showViewSelector && (
        <div className='flex w-full min-w-0 items-center gap-1 sm:w-auto'>
          <Select
            value={externalEmrView ? 'external' : 'roger'}
            onValueChange={value => onExternalEmrView(value === 'external')}
          >
            <SelectTrigger
              data-testid='external-emr-view-toggle'
              className='w-full sm:w-56'
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='roger'>{ROGER_CHARTS_LABEL}</SelectItem>
              <SelectItem value='external'>
                {EXTERNAL_EMR_CHARTS_LABEL}
              </SelectItem>
            </SelectContent>
          </Select>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='iconMd'
                  aria-label='About this patient list'
                  data-testid='external-emr-view-help'
                  className='shrink-0'
                >
                  <Info />
                </Button>
              </TooltipTrigger>
              <TooltipContent side='bottom' className='max-w-xs'>
                {EXTERNAL_EMR_VIEW_HELP}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
}
